import { IManualAddressFormOutput } from 'shared/components/form/address-search/manual-address-form';
import {
  IAddress,
  IAddressSearchSuggestion,
  IIdealPostcodeAutocompleteSuggestions,
  IIdealPostcodeResolveResponse,
} from './address-api-interface';
import { createAddressString } from 'core/utilities/address-string-builder';

const API_KEY = process.env.REACT_APP_IP_KEY;

const autocomplete = async (term: string): Promise<IAddressSearchSuggestion[]> => {
  try {
    const url = `https://api.ideal-postcodes.co.uk/v1/autocomplete/addresses?api_key=${API_KEY}&query=${encodeURIComponent(term)}&context=GBR&limit=100`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    const json = (await response.json()) as IIdealPostcodeAutocompleteSuggestions;
    if (!json.result?.hits) {
      return [];
    }
    return json.result.hits.map((hit) => ({
      address: hit.suggestion,
      id: hit.id,
    }));
  } catch (error) {
    console.log(error);
    throw error;
  }
};

const getFullAddress = async (id: string): Promise<IIdealPostcodeResolveResponse> => {
  try {
    const url = `https://api.ideal-postcodes.co.uk/v1/autocomplete/addresses/${id}/gbr?api_key=${API_KEY}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    const json = await response.json();
    return json as IIdealPostcodeResolveResponse;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

const getFullAddressFromAddressFormOutputGoogle = async (
  address: Partial<IManualAddressFormOutput>,
): Promise<IAddress | undefined> => {
  try {
    if (!address.addressLine1 || !address.postcode) {
      throw new Error('No address line 1 or postcode');
    }
    const formattedAddress = createAddressString([
      address.addressLine1,
      address.addressLine2,
      address.addressLine3,
      address.city,
      address.postcode,
    ]);
    const geocoder = new google.maps.Geocoder();
    const response = await geocoder.geocode({ address: formattedAddress });

    if (!response || response.results.length === 0) {
      throw new Error('No results from geocode');
    }
    const place = response.results[0];

    return {
      postcode: address.postcode,
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
      formatted_address: formattedAddress.split(', '),
      thoroughfare: '',
      building_name: '',
      sub_building_name: '',
      sub_building_number: '',
      building_number: 0,
      line_1: address.addressLine1,
      line_2: address.addressLine2 || '',
      line_3: address.addressLine3 || '',
      line_4: '',
      locality: '',
      town_or_city: address.city || '',
      county: '',
      district: '',
      country: '',
      residential: false,
    };
  } catch (error) {
    throw error;
  }
};

const AddressApiService = {
  autocomplete,
  getFullAddress,
  getFullAddressFromAddressFormOutputGoogle,
};

export default AddressApiService;
